import { Box, Typography } from "@mui/material";
import { useState, useEffect, useRef, ReactElement } from "react";
import styled from "styled-components";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { colors } from "../../common/colors";
import SelectedUser from "../items/SelectedUser";
import profile from "../../assets/images/profile_img.png";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import "../../App.css";
import { useSelector } from "react-redux";
import LoadingModal from "../LoadingModal";
import appServices from "../../services/app-services/appServices";
import axios, { CancelTokenSource } from "axios";
import { convertUserImage } from "../../util/utils";
import { PrivateUser, UserEntity } from "../../util/CreatePost/types";
import { T } from "react-translator-component";
import { isSamePrivateUser } from "../../util/CreatePost/utils";
import dayjs from "../../util/dayjs";

let width = window.innerWidth * 0.9;
let height = window.innerHeight - 200;
// Styled components
const Container = styled.div`
  width: ${width}
  margin: auto;
  padding: 20px;
  padding-top:0;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  overflow:auto
`;

const SearchField = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const UserList = styled.ul`
  list-style: none;
  padding: 0;
  overflow: auto; /* Corrected overflow property */
  max-height: ${height}; /* Set a max height to enable scrolling */
`;

const UserItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 10px;
`;

const AddButton = styled.button`
  margin-left: auto;
  border-color: ${colors.farm_green500};
  border-radius: 20px;
  border-width: 2px;
  width: 40px;
  height: 40px;
  color: ${colors.farm_green500};
`;
const ParentDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const ClearButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0;
  background-color: white;
`;

// Main component

interface Props {
  isEdit: boolean;
  onAdd: (user: any) => void;
  onDelete: (user: UserEntity) => void;
  closeModal:() => void;
}

export default function AddUserModal({ isEdit, onAdd, onDelete, closeModal }: Props): ReactElement {
  const scrollRef = useRef<any>(null);
  const { user } = useSelector((state: any) => state.userAuth);
  const post = useSelector((state: any) => state.createPost.values);
  const assignedPostPrivateUsers = post.privateUsers.filter((privateUser: PrivateUser) => privateUser._destroy !== 1)

  const [users, setUsers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const handleSearch = async (value: string) => {
    setSearchTerm(value);
    if (!value) {
      setUsers([])
      return
    }

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled due to new request.");
    }
    cancelTokenRef.current = axios.CancelToken.source();

    await searchUsers(value, cancelTokenRef.current.token)
  };

  const searchUsers = async (term: string, token: any) => {
    const cleanedTerm = term.replace(/^[\s\u3000]+/, '');

    try {
      setIsLoading(true);
      const response = await appServices.searchUsers(cleanedTerm, null, token, true);
      const resUsers = response.users
      const navigators = response.navigators
      const userList = resUsers.concat(navigators)
        .sort((a: any, b: any) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf())

      setUsers(userList);
    } catch (error) {
      if (axios.isCancel(error)) {
        // pass
      } else {
        console.error("Error fetching search date:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isUserSelected = (searchUser: any) => {
    return post.privateUsers.some(
      (member: PrivateUser) =>
        member._destroy === 0 && isSamePrivateUser(member, searchUser.class_name, searchUser.id)
    );
  }

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [post.privateUsers]);

  return (
    <Container>
      <LoadingModal visible={isLoading} />
      <ParentDiv>
        <ClearButton onClick={closeModal}>
          <Typography
            sx={{ fontSize: 12, color: colors.farm_gray200 }}
            variant="body2"
            color="textPrimary"
          >
            {T("close")}
          </Typography>
          <KeyboardArrowDownIcon />
        </ClearButton>
      </ParentDiv>
      <div>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" color="textPrimary">
            {T("post_private_users")}
          </Typography>
          <Typography
            variant="body2"
            style={{
              backgroundColor: colors.farm_blue200,
              borderRadius: "4px",
              color: "white",
              padding: "2px 8px",
              marginLeft: "8px",
              marginBottom: "2px",
            }}
          >
            {assignedPostPrivateUsers.length}
          </Typography>
        </Box>
        <Carousel
          responsive={{
            superLargeDesktop: {
              breakpoint: { max: 4000, min: 1024 },
              items: 5, // Number of items to show on large screens
            },
            desktop: { breakpoint: { max: 1024, min: 768 }, items: 3 },
            tablet: { breakpoint: { max: 768, min: 464 }, items: 2 },

            mobile: {
              breakpoint: { max: 464, min: 0 },
              items: 2, // Ensure at least 2 items on mobile screens
            },
          }}
          swipeable={true} // Allows swipe gestures on mobile devices
          draggable={true} // Enables dragging on desktop
          showDots={false} // Optionally show/hide dots
          arrows={true} // Optionally show/hide arrows
          infinite={false} // Optionally make the carousel infinite
          keyBoardControl={true} // Allows keyboard control
          itemClass="carousel-item-padding" // Item class
        >
          {assignedPostPrivateUsers.slice().reverse().map((privateUser: PrivateUser) => (
            <SelectedUser
              isEdit={isEdit}
              privateUser={privateUser}
              key={privateUser.userId}
              onDelete={() => onDelete(privateUser)}
            />
          ))}
        </Carousel>
      </div>

      <SearchField
        type="text"
        value={searchTerm}
        onChange={(e) => handleSearch(e.target.value)}
      />

      <UserList>
        {users.map((userAll: any) => {
          return (
            <UserItem key={`${userAll.class_name}_${userAll.id}`}>
              <Avatar
                src={userAll?.image ? convertUserImage(userAll.image) : profile}
                alt={userAll.nickname}
              />
              {userAll.nickname}
              {!isUserSelected(userAll) && (user.class_name !== userAll.class_name || user.id !== userAll.id) && (
                <AddButton onClick={() => onAdd(userAll)}>
                  +
                </AddButton>
              )}
            </UserItem>
          );
        })}
      </UserList>
    </Container>
  );
};
